import { useEffect, useMemo, useRef, useState } from "react";
import {
  AdvancedMarker,
  Circle,
  Map as GoogleMap,
  useMap,
  useMapsLibrary,
  type MapMouseEvent,
} from "@vis.gl/react-google-maps";
import { Loader2, Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { GoogleMapsGate } from "@/components/maps/GoogleMapsGate";
import { DEFAULT_CENTER, GOOGLE_MAPS_MAP_ID, primaryColor } from "@/lib/google-maps";

type Props = {
  latitude: number;
  longitude: number;
  radiusM: number;
  onChange: (next: { latitude: number; longitude: number }) => void;
  /**
   * Sizes the map box. Defaults to a fixed 288px; pass flex or min-height
   * classes to let the layout decide — the Site form has the map fill its card.
   */
  className?: string;
};

/**
 * Pan to the pin when its coordinates change from outside the map — typed into
 * the latitude/longitude fields, or picked from search.
 *
 * Deliberately a pan rather than a controlled `center`: a controlled centre
 * would snap back every time the admin dragged the map to look around.
 */
function FollowPin({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map?.panTo({ lat, lng });
  }, [map, lat, lng]);
  return null;
}

/**
 * Address search on Google Places (New).
 *
 * Search-as-you-type is fine here, and was not before: Places Autocomplete is
 * built for it and billed per *session*, whereas OpenStreetMap's Nominatim
 * explicitly forbids client-side autocomplete and caps usage at one request per
 * second. One session token covers every keystroke of a search plus the final
 * lookup, then is discarded, so a search costs one session however much typing
 * it took.
 */
function PlaceSearch({
  near,
  onPick,
}: {
  near: google.maps.LatLngLiteral;
  onPick: (p: { latitude: number; longitude: number }) => void;
}) {
  const places = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<google.maps.places.PlacePrediction[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  /**
   * Why search is not working, when it is not. A failure used to clear the
   * results and say nothing, which reads exactly like "no such place" — the
   * same silent-failure shape as the map bug this component was rewritten for.
   */
  const [failure, setFailure] = useState<string | null>(null);
  const token = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  /**
   * Text this component put in the box itself, after a pick. Searching it again
   * would reopen the list over the map the admin just chose from — and on
   * Google it would open a second billed session for nothing.
   */
  const programmatic = useRef<string | null>(null);
  // Read at request time without restarting the search every time the pin moves.
  const bias = useRef(near);
  bias.current = near;

  useEffect(() => {
    if (programmatic.current !== null && query === programmatic.current) return;
    const q = query.trim();
    if (!places || q.length < 3) {
      setResults([]);
      setSearching(false);
      setFailure(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      token.current ??= new places.AutocompleteSessionToken();
      setSearching(true);
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken: token.current,
          // Biased, not restricted: local results rank first, but a location
          // outside Malaysia can still be found if one is ever needed.
          locationBias: bias.current,
          region: "my",
        });
        if (cancelled) return;
        setFailure(null);
        setResults(
          suggestions
            .map((s) => s.placePrediction)
            .filter((p): p is google.maps.places.PlacePrediction => p != null),
        );
        setOpen(true);
      } catch (e) {
        if (cancelled) return;
        setResults([]);
        setOpen(true);
        // The API-not-enabled case is the one an operator can fix, and the one
        // a fresh Google Cloud project hits first, so name it.
        const msg = String((e as Error)?.message ?? "");
        setFailure(
          /has not been used|is disabled|not authorized|PERMISSION_DENIED|API_KEY/i.test(msg)
            ? "Address search is not enabled for this map key. Enable \u201cPlaces API (New)\u201d in Google Cloud."
            : "Address search is unavailable right now. You can still click the map or type coordinates.",
        );
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, places]);

  const pick = async (prediction: google.maps.places.PlacePrediction) => {
    setOpen(false);
    setResults([]);
    programmatic.current = prediction.text.text;
    setQuery(prediction.text.text);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["location", "formattedAddress"] });
      const loc = place.location;
      if (loc) onPick({ latitude: loc.lat(), longitude: loc.lng() });
      if (place.formattedAddress) {
        programmatic.current = place.formattedAddress;
        setQuery(place.formattedAddress);
      }
    } finally {
      // The lookup closes the session; the next search starts a fresh one.
      token.current = null;
    }
  };

  return (
    <div className="absolute left-2 right-2 top-2 z-10">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => {
            // Anything typed is a real search again, even if it happens to
            // match the address a pick left behind.
            programmatic.current = null;
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Search address or place…"
          className="h-9 bg-background/95 pl-9 pr-9 shadow-sm backdrop-blur-sm"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && failure && (
        <div
          role="status"
          className="mt-1 rounded-md border border-warning/40 bg-popover px-3 py-2 text-xs text-muted-foreground shadow-md"
        >
          {failure}
        </div>
      )}
      {open && !failure && results.length > 0 && (
        <div className="mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover shadow-md">
          {results.map((r) => (
            <button
              key={r.placeId}
              type="button"
              onClick={() => void pick(r)}
              className="block w-full px-3 py-2 text-left hover:bg-accent"
              title={r.text.text}
            >
              <span className="block truncate text-xs font-medium">
                {r.mainText?.text ?? r.text.text}
              </span>
              {r.secondaryText?.text && (
                <span className="block truncate text-[11px] text-muted-foreground">
                  {r.secondaryText.text}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PickerMap({ latitude, longitude, radiusM, onChange, className }: Props) {
  const pin = useMemo(
    () => ({
      lat: Number.isFinite(latitude) ? latitude : DEFAULT_CENTER.lat,
      lng: Number.isFinite(longitude) ? longitude : DEFAULT_CENTER.lng,
    }),
    [latitude, longitude],
  );
  const color = useMemo(primaryColor, []);

  const placeAt = (e: MapMouseEvent) => {
    const at = e.detail.latLng;
    if (at) onChange({ latitude: at.lat, longitude: at.lng });
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border ${className ?? "h-72"}`}>
      <PlaceSearch near={pin} onPick={onChange} />

      <GoogleMap
        mapId={GOOGLE_MAPS_MAP_ID}
        defaultCenter={pin}
        defaultZoom={16}
        gestureHandling="greedy"
        disableDefaultUI
        zoomControl
        clickableIcons={false}
        onClick={placeAt}
        // Pinned to the box rather than sized as a percentage: a percentage
        // height collapses when the box's height comes from flex or
        // min-height, which is exactly how a layout that fills a card sizes it.
        style={{ position: "absolute", inset: 0 }}
      >
        <FollowPin lat={pin.lat} lng={pin.lng} />
        <AdvancedMarker
          position={pin}
          draggable
          title="Drag to move the work location"
          onDragEnd={(e) => {
            const at = e.latLng;
            if (at) onChange({ latitude: at.lat(), longitude: at.lng() });
          }}
        />
        {/* Not clickable, so a click inside the circle still moves the pin —
            otherwise the geofence would swallow exactly the clicks an admin
            uses to nudge the location. */}
        <Circle
          center={pin}
          radius={radiusM}
          clickable={false}
          strokeColor={color}
          strokeWeight={2}
          fillColor={color}
          fillOpacity={0.15}
        />
      </GoogleMap>

      <div className="pointer-events-none absolute bottom-2 left-2 z-10 rounded-md bg-background/90 px-2 py-1 font-mono text-[10px] text-muted-foreground backdrop-blur-sm">
        {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)} · {radiusM}m
      </div>
    </div>
  );
}

export function GeofenceMapPicker(props: Props) {
  return (
    <GoogleMapsGate className={props.className ?? "h-72"}>
      <PickerMap {...props} />
    </GoogleMapsGate>
  );
}
